/**
 * The zones the Rules card offers (console spec §5.2): every `America/` zone
 * the runtime knows, and always the tournament's current one — which keeps a
 * zone set by hand, or by an older browser, from silently reading as its
 * first neighbour in the list.
 */
export function supportedZones(): string[] {
  const intl = Intl as unknown as { supportedValuesOf?: (key: string) => string[] };
  try {
    return intl.supportedValuesOf ? intl.supportedValuesOf("timeZone") : [];
  } catch {
    return [];
  }
}

export function timezoneOptions(current: string, all: string[] = supportedZones()): string[] {
  const zones = new Set(all.filter((z) => z.startsWith("America/")));
  if (current) zones.add(current);
  return [...zones].sort();
}

/**
 * The zones a US event runs in, east to west, by the names a coach knows them
 * by. The create flow offers these rather than `timezoneOptions`: its events
 * are in a US state, and a phone-width list of every `America/` zone buries the
 * seven that apply among Canada, the Caribbean, and South America.
 */
export const US_TIMEZONES = [
  { zone: "America/New_York", label: "Eastern" },
  { zone: "America/Chicago", label: "Central" },
  { zone: "America/Denver", label: "Mountain" },
  { zone: "America/Phoenix", label: "Arizona" },
  { zone: "America/Los_Angeles", label: "Pacific" },
  { zone: "America/Anchorage", label: "Alaska" },
  { zone: "Pacific/Honolulu", label: "Hawaii" },
] as const;

type UsTimezone = (typeof US_TIMEZONES)[number]["zone"];

/**
 * The zone most of each state keeps. Only a default: several states span two
 * zones (west Texas, the Florida panhandle, East Tennessee, north Idaho), so
 * the create flow lets the coach change it.
 */
const STATE_TIMEZONE: Record<string, UsTimezone> = {
  AL: "America/Chicago",
  AK: "America/Anchorage",
  AZ: "America/Phoenix",
  AR: "America/Chicago",
  CA: "America/Los_Angeles",
  CO: "America/Denver",
  CT: "America/New_York",
  DE: "America/New_York",
  DC: "America/New_York",
  FL: "America/New_York",
  GA: "America/New_York",
  HI: "Pacific/Honolulu",
  ID: "America/Denver",
  IL: "America/Chicago",
  IN: "America/New_York",
  IA: "America/Chicago",
  KS: "America/Chicago",
  KY: "America/New_York",
  LA: "America/Chicago",
  ME: "America/New_York",
  MD: "America/New_York",
  MA: "America/New_York",
  MI: "America/New_York",
  MN: "America/Chicago",
  MS: "America/Chicago",
  MO: "America/Chicago",
  MT: "America/Denver",
  NE: "America/Chicago",
  NV: "America/Los_Angeles",
  NH: "America/New_York",
  NJ: "America/New_York",
  NM: "America/Denver",
  NY: "America/New_York",
  NC: "America/New_York",
  ND: "America/Chicago",
  OH: "America/New_York",
  OK: "America/Chicago",
  OR: "America/Los_Angeles",
  PA: "America/New_York",
  RI: "America/New_York",
  SC: "America/New_York",
  SD: "America/Chicago",
  TN: "America/Chicago",
  TX: "America/Chicago",
  UT: "America/Denver",
  VT: "America/New_York",
  VA: "America/New_York",
  WA: "America/Los_Angeles",
  WV: "America/New_York",
  WI: "America/Chicago",
  WY: "America/Denver",
};

/** A state's default zone, or "" for no state. */
export function stateTimezone(state: string): string {
  return STATE_TIMEZONE[state] ?? "";
}

/** "Pacific" for a listed zone; any other zone by its IANA id. */
export function timezoneLabel(zone: string): string {
  return US_TIMEZONES.find((z) => z.zone === zone)?.label ?? zone;
}
